import { z } from 'zod';
import { dayKeySchema, timeOfDaySchema } from './contacts.js';
/**
 * Enums, labels, the smart-view filter catalog and the request schemas of the dialer and the telephony
 * module (Terminierung 01–11 incl. 08b, Controlling 05, Kontaktakte E1 softphone) shared by the API
 * (validation) and the SPA (filter editor, disposition chips, forms). German strings verbatim from the
 * screens; identifiers English (docs/glossary.md). Response shapes: docs/dev/api-dialer.md.
 */
// --- lists -----------------------------------------------------------------------------------------
export const callListTypes = ['smart', 'manual', 'campaign', 'import'];
/** Group headers and notes of Terminierung 04. */
export const callListGroupLabels = {
    smart: { label: 'Smart Views', note: 'Folgen Filtern, Bestand ändert sich automatisch' },
    manual: { label: 'Manuelle Listen', note: 'Kontakte werden von Hand hinzugefügt und entfernt' },
    campaign: { label: 'Kampagnen', note: 'Füllen sich automatisch über Webhook oder API' },
    import: {
        label: 'Importlisten',
        note: 'Aus Excel- oder CSV-Dateien, ohne die Kontaktliste zu füllen',
    },
};
/** Eyebrow of the list detail (Terminierung 05–07). */
export const callListTypeLabels = {
    smart: 'Smart View',
    manual: 'Manuelle Liste',
    campaign: 'Kampagne',
    import: 'Importliste',
};
export const callListSortRules = ['newest_first', 'oldest_first'];
export const callListSortRuleLabels = {
    newest_first: 'neueste Leads zuerst',
    oldest_first: 'älteste Leads zuerst',
};
/** Lifecycle keys that are dialled (A5.1: Neu, Kontaktiert, Wiedervorlage; „Qualifiziert" has no stage). */
export const dialableLifecycleKeys = ['new', 'contacted', 'follow_up'];
/** Why a contact of a list is skipped (Terminierung 05/06 column „Wählbar"). */
export const dialableReasons = ['Status', 'Zeitfenster', 'Gesperrt'];
// --- smart-view filters (Terminierung 05) ----------------------------------------------------------
export const listFilterFields = [
    'lifecycle',
    'lead_source',
    'last_call_attempt',
    'call_attempts',
    'owner',
    'property',
    'equity',
    'created_within',
    'no_open_appointment',
];
export const listFilterOperators = [
    'is',
    'is_not',
    'is_empty',
    'older_than',
    'within',
    'less_than',
    'at_least',
    'equals',
    'none',
];
/** Field, operator and value labels of the filter editor; all conditions are AND-combined. */
export const listFilterCatalog = {
    lifecycle: {
        label: 'Lifecycle-Status',
        operators: [
            { key: 'is', label: 'ist', value: 'lifecycle_keys' },
            { key: 'is_not', label: 'ist nicht', value: 'lifecycle_keys' },
        ],
    },
    lead_source: {
        label: 'Lead-Quelle/Brand',
        operators: [{ key: 'is', label: 'ist', value: 'text' }],
    },
    last_call_attempt: {
        label: 'Letzter Anrufversuch',
        operators: [
            { key: 'older_than', label: 'älter als', value: 'days' },
            { key: 'within', label: 'innerhalb von', value: 'days' },
        ],
    },
    call_attempts: {
        label: 'Anzahl Versuche',
        operators: [
            { key: 'less_than', label: 'kleiner als', value: 'count' },
            { key: 'at_least', label: 'mindestens', value: 'count' },
            { key: 'equals', label: 'gleich', value: 'count' },
        ],
    },
    owner: {
        label: 'Betreuer',
        operators: [
            { key: 'is', label: 'ist', value: 'user_ids' },
            { key: 'is_empty', label: 'ist leer (Dialer-Pool)', value: 'none' },
        ],
    },
    property: {
        label: 'Objekt/Thema',
        operators: [{ key: 'is', label: 'ist', value: 'property_id' }],
    },
    equity: {
        label: 'Eigenkapital',
        operators: [{ key: 'at_least', label: 'mindestens', value: 'cents' }],
    },
    created_within: {
        label: 'Angelegt',
        operators: [{ key: 'within', label: 'innerhalb von', value: 'days' }],
    },
    no_open_appointment: {
        label: 'Termin',
        operators: [{ key: 'none', label: 'kein Termin gebucht', value: 'none' }],
    },
};
const uuidPattern = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;
const lifecyclePattern = /^(new|contacted|call_booked|prospect|customer|existing_customer|follow_up|no_show|not_reached|no_interest|disqualified)$/;
/** Checks a value against its kind; returns the German error or null. */
export function listFilterValueError(kind, value) {
    if (kind === 'none')
        return value === null || value === '' ? null : 'Diese Bedingung hat keinen Wert.';
    if (value === null || value.trim() === '')
        return 'Bitte einen Wert angeben.';
    const parts = value.split(',').map((part) => part.trim());
    switch (kind) {
        case 'lifecycle_keys':
            return parts.every((part) => lifecyclePattern.test(part)) ? null : 'Unbekannter Status.';
        case 'user_ids':
            return parts.every((part) => uuidPattern.test(part)) ? null : 'Bitte Betreuer wählen.';
        case 'property_id':
            return uuidPattern.test(value.trim()) ? null : 'Bitte ein Objekt wählen.';
        case 'days':
            return /^\d{1,4}$/.test(value.trim()) && Number(value) > 0 ? null : 'Bitte eine Anzahl Tage angeben.';
        case 'count':
            return /^\d{1,4}$/.test(value.trim()) ? null : 'Bitte eine Zahl angeben.';
        case 'cents':
            return /^\d{1,12}$/.test(value.trim()) ? null : 'Bitte einen Betrag angeben.';
        case 'text':
            return value.trim().length <= 200 ? null : 'Höchstens 200 Zeichen.';
    }
}
export const listFilterSchema = z
    .object({
    field: z.enum(listFilterFields),
    operator: z.enum(listFilterOperators),
    value: z.string().trim().max(2000).nullable().default(null),
})
    .superRefine((filter, context) => {
    const spec = listFilterCatalog[filter.field].operators.find((op) => op.key === filter.operator);
    if (spec === undefined) {
        context.addIssue({
            code: 'custom',
            path: ['operator'],
            message: 'Dieser Operator passt nicht zum Feld.',
        });
        return;
    }
    const error = listFilterValueError(spec.value, filter.value);
    if (error !== null)
        context.addIssue({ code: 'custom', path: ['value'], message: error });
});
export const listFiltersSchema = z.array(listFilterSchema).max(20);
const listSettingsFields = {
    sortRule: z.enum(callListSortRules).optional(),
    followUpsFirst: z.boolean().optional(),
    timeWindowStart: timeOfDaySchema.optional(),
    timeWindowEnd: timeOfDaySchema.optional(),
};
export const createCallListBodySchema = z.object({
    type: z.enum(['smart', 'manual']),
    name: z.string().trim().min(1, 'Pflichtangabe.').max(120),
    eventTypeId: z.string().uuid('Bitte wählen Sie einen Ereignistyp.'),
    filters: listFiltersSchema.optional(),
    setterIds: z.array(z.string().uuid()).max(50).optional(),
    ...listSettingsFields,
});
export const updateCallListBodySchema = z.object({
    name: z.string().trim().min(1, 'Pflichtangabe.').max(120).optional(),
    eventTypeId: z.string().uuid('Bitte wählen Sie einen Ereignistyp.').optional(),
    active: z.boolean().optional(),
    ...listSettingsFields,
});
export const putListFiltersBodySchema = z.object({ filters: listFiltersSchema });
export const putListSettersBodySchema = z.object({ userIds: z.array(z.string().uuid()).max(50) });
export const addListContactsBodySchema = z.object({
    contactIds: z.array(z.string().uuid()).min(1, 'Bitte Kontakte wählen.').max(500),
});
export const previewCountBodySchema = z.object({ filters: listFiltersSchema });
// --- campaigns (Terminierung 07) -------------------------------------------------------------------
export const campaignSourceTypes = ['webhook', 'api'];
export const createCampaignBodySchema = z.object({
    name: z.string().trim().min(1, 'Pflichtangabe.').max(120),
    eventTypeId: z.string().uuid('Bitte wählen Sie einen Ereignistyp.'),
    propertyId: z.string().uuid().nullable().optional(),
    leadSource: z.string().trim().max(120).nullable().optional(),
    utmCampaign: z.string().trim().max(120).nullable().optional(),
    costCents: z.number().int().min(0).nullable().optional(),
    setterIds: z.array(z.string().uuid()).max(50).optional(),
    priority: z.number().int().min(0).max(1).optional(),
});
export const updateCampaignBodySchema = z.object({
    name: z.string().trim().min(1, 'Pflichtangabe.').max(120).optional(),
    active: z.boolean().optional(),
    propertyId: z.string().uuid().nullable().optional(),
    leadSource: z.string().trim().max(120).nullable().optional(),
    utmCampaign: z.string().trim().max(120).nullable().optional(),
    costCents: z.number().int().min(0).nullable().optional(),
    /** 0 normal, 1 „Prio-Kampagne" (D-208). */
    priority: z.number().int().min(0).max(1).optional(),
});
export const createCampaignSourceBodySchema = z.object({
    name: z.string().trim().min(1, 'Pflichtangabe.').max(120),
    type: z.enum(campaignSourceTypes),
});
export const updateCampaignSourceBodySchema = z.object({ active: z.boolean() });
/** The six lead answers of Terminierung 08 „Angaben aus der Eintragung". */
export const leadAnswerKeys = [
    'interest',
    'employment',
    'income',
    'equity',
    'timing',
    'reachability',
];
export const leadAnswerLabels = {
    interest: 'Interesse',
    employment: 'Beschäftigung',
    income: 'Einkommen',
    equity: 'Eigenkapital',
    timing: 'Timing',
    reachability: 'Erreichbar',
};
// --- sessions, booking, dispositions (Terminierung 01–11) ------------------------------------------
export const dialModes = ['preview', 'power'];
/** Queue bar pill and preview fact (A3). */
export const dialModeLabels = {
    power: { pill: 'Power-Dialer', fact: 'Power · wählt automatisch' },
    preview: { pill: 'Preview', fact: 'Preview · Setter startet' },
};
export const startDialerSessionBodySchema = z.object({
    listIds: z.array(z.string().uuid()).max(50).optional(),
    dialMode: z.enum(dialModes).default('power'),
});
export const dialerBookBodySchema = z.object({
    eventTypeId: z.string().uuid('Bitte wählen Sie einen Ereignistyp.'),
    startsAt: z.string().datetime({ offset: true }),
    advisorId: z.string().uuid().optional(),
});
/** The six chips of Terminierung 10 (the `call_disposition` enum keeps three more for the record's E1). */
export const dialerDispositions = [
    'appointment_set',
    'follow_up',
    'not_reached',
    'no_interest',
    'wrong_number',
    'block',
];
export const dialerDispositionLabels = {
    appointment_set: 'Termin vereinbart',
    follow_up: 'Wiedervorlage',
    not_reached: 'Nicht erreicht',
    no_interest: 'Kein Interesse',
    wrong_number: 'Falsche Nummer',
    block: 'Sperren',
};
export const dialerDispositionBodySchema = z.object({
    disposition: z.enum(dialerDispositions),
    note: z.string().trim().max(4000).optional(),
    followUp: z.object({ dueOn: dayKeySchema, dueTime: timeOfDaySchema.optional() }).optional(),
    /** Reason of „Kein Interesse" / „Sperren" (falls back to the note). */
    reason: z.string().trim().max(1000).optional(),
});
// --- telephony (softphone) -------------------------------------------------------------------------
export const callSources = ['record', 'dialer'];
export const startCallBodySchema = z.object({
    contactId: z.string().uuid(),
    contactPhoneId: z.string().uuid().optional(),
    source: z.enum(callSources),
    sessionId: z.string().uuid().optional(),
    taskId: z.string().uuid().optional(),
});
export const callEventTypes = ['ringing', 'connected', 'ended', 'failed'];
export const callEventBodySchema = z.object({
    type: z.enum(callEventTypes),
    at: z.string().datetime({ offset: true }),
    reason: z.string().trim().max(200).optional(),
});
export const callStatuses = [
    'started',
    'ringing',
    'connected',
    'ended',
    'failed',
    'not_reached',
    'voicemail',
];
// --- setter statistics (Controlling 05) ------------------------------------------------------------
export const statisticsPeriods = ['week', 'month'];
export const statisticsPeriodLabels = { week: 'Woche', month: 'Monat' };
// --- figures of the metrics bar ---------------------------------------------------------------------
const oneDecimal = new Intl.NumberFormat('de-DE', { minimumFractionDigits: 1, maximumFractionDigits: 1 });
/** 2.08 → "2,1" (Termine / Std. Talktime). */
export function formatOneDecimal(value) {
    return oneDecimal.format(value);
}
/** 7440 s → "2:04" (hours:minutes of talk time; the unit „Std." is the cell's sub text). */
export function formatTalkTime(seconds) {
    const minutes = Math.floor(Math.max(0, seconds) / 60);
    return `${Math.floor(minutes / 60)}:${String(minutes % 60).padStart(2, '0')}`;
}
/** 112 800 s → "31 h 20 min" (Controlling 05 „Gesprächszeit"). */
export function formatTalkTimeLong(seconds) {
    const minutes = Math.floor(Math.max(0, seconds) / 60);
    return `${Math.floor(minutes / 60)} h ${minutes % 60} min`;
}
/** 401 s → "6:41" (call duration in the timeline and the disposition header). */
export function formatCallDuration(seconds) {
    const whole = Math.max(0, Math.round(seconds));
    return `${Math.floor(whole / 60)}:${String(whole % 60).padStart(2, '0')}`;
}
// --- phone display ---------------------------------------------------------------------------------
/**
 * „+49 171 482 93 07" from E.164 (display only): country code, three digits, then the rest in pairs
 * with a leading group of three when the count is odd. Other notations are returned unchanged.
 */
export function formatPhoneDisplay(e164) {
    const match = /^\+(49|43|41)(\d{3})(\d+)$/.exec(e164);
    if (match === null)
        return e164;
    const [, country, prefix, rest] = match;
    const groups = [];
    let remaining = rest;
    if (remaining.length % 2 === 1 && remaining.length >= 3) {
        groups.push(remaining.slice(0, 3));
        remaining = remaining.slice(3);
    }
    for (let index = 0; index < remaining.length; index += 2)
        groups.push(remaining.slice(index, index + 2));
    return `+${country} ${prefix} ${groups.join(' ')}`;
}
//# sourceMappingURL=dialer.js.map